import { CONTEXT_TAGS, TIME_OF_DAY } from "@shared/schema";
import { Moon, Sun, Sunset, Clock } from "lucide-react";

export const TIME_ICONS: Record<string, typeof Sun> = {
  morning: Sun,
  afternoon: Sun,
  evening: Sunset,
  "late night": Moon,
};

export const INTENSITY_LABELS = [
  "barely there",
  "background hum",
  "noticeable",
  "hard to ignore",
  "defining the day",
];

/**
 * Intensity control.
 * Layout: current value word centered above the track; LOW / HIGH anchored
 * at the track ends. The track is full-width (flex-1) so the dot positions
 * and the value word always agree.
 */
export function IntensitySlider({ value, onChange, accentColor }: {
  value: number;
  onChange: (v: number) => void;
  accentColor: string;
}) {
  const steps = 5;
  const labelIdx = Math.min(Math.round(value * (steps - 1)), steps - 1);

  return (
    <div className="w-full mb-2">
      <div className="text-center mb-1.5">
        <span
          style={{ color: accentColor, transition: "color 0.3s ease", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
          data-testid="text-intensity-label"
        >
          {INTENSITY_LABELS[labelIdx]}
        </span>
      </div>
      <div className="flex items-center gap-3 w-full">
        <span
          className="shrink-0 font-semibold"
          style={{
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--type-label-size)",
            letterSpacing: "var(--type-label-tracking)",
            textTransform: "uppercase",
          }}
        >
          LOW
        </span>
        <div className="relative h-8 flex items-center flex-1">
          <div className="absolute w-full h-0.5 rounded-full" style={{ background: "var(--border-hairline)" }} />
          <div
            className="absolute h-0.5 left-0 rounded-full"
            style={{ width: `${value * 100}%`, backgroundColor: accentColor, transition: "width 0.15s ease, background-color 0.3s ease" }}
          />
          {Array.from({ length: steps }).map((_, i) => {
            const pos = i / (steps - 1);
            const isActive = value >= pos - 0.05;
            return (
              <button
                key={i}
                onClick={() => onChange(pos)}
                className="absolute w-11 h-11 -translate-x-1/2 transition-all duration-200 flex items-center justify-center"
                style={{
                  left: `${pos * 100}%`,
                }}
                data-testid={`intensity-step-${i}`}
              >
                <span
                  className="block w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: isActive ? accentColor : "var(--surface-3)",
                    border: `2px solid ${isActive ? accentColor : "var(--border-light)"}`,
                  }}
                />
              </button>
            );
          })}
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={value}
            onChange={e => onChange(parseFloat(e.target.value))}
            className="absolute w-full h-11 opacity-0 cursor-pointer"
            data-testid="input-intensity"
          />
        </div>
        <span
          className="shrink-0"
          style={{
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--type-label-size)",
            letterSpacing: "var(--type-label-tracking)",
            textTransform: "uppercase",
          }}
        >
          HIGH
        </span>
      </div>
    </div>
  );
}

export function TimeOfDaySelector({ value, onChange, accentColor }: {
  value: string | null;
  onChange: (t: string) => void;
  accentColor: string;
}) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="time-of-day-selector">
      {TIME_OF_DAY.map(time => {
        const isSelected = value === time;
        const Icon = TIME_ICONS[time] || Clock;
        return (
          <button
            key={time}
            onClick={() => onChange(time)}
            data-testid={`time-${time.replace(" ", "-")}`}
            className="touch-target flex items-center gap-1.5 px-3 py-1.5 transition-all duration-200"
            style={{
              color: isSelected ? accentColor : "var(--text-muted)",
              border: `1px solid ${isSelected ? `color-mix(in srgb, ${accentColor} 25%, transparent)` : "var(--border-light)"}`,
              background: isSelected ? `color-mix(in srgb, ${accentColor} 5%, transparent)` : "transparent",
              borderRadius: "8px",
              fontFamily: "var(--font-display)",
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            <Icon size={11} />
            {time.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

export function ContextTagPicker({ selected, onToggle, accentColor }: {
  selected: string[];
  onToggle: (tag: string) => void;
  accentColor: string;
}) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="context-tags">
      {CONTEXT_TAGS.map(tag => {
        const isSelected = selected.includes(tag);
        return (
          <button
            key={tag}
            onClick={() => onToggle(tag)}
            data-testid={`tag-${tag.toLowerCase()}`}
            className="touch-target px-3 py-1.5 transition-all duration-200"
            style={{
              color: isSelected ? accentColor : "var(--text-muted)",
              border: `1px solid ${isSelected ? `color-mix(in srgb, ${accentColor} 25%, transparent)` : "var(--border-light)"}`,
              background: isSelected ? `color-mix(in srgb, ${accentColor} 5%, transparent)` : "transparent",
              borderRadius: "8px",
              fontFamily: "var(--font-display)",
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {tag.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}
